import careConfig from "@careConfig";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { navigate } from "raviger";
import { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import CareIcon from "@/CAREUI/icons/CareIcon";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { Avatar } from "@/components/Common/Avatar";

import { usePatientSignOut } from "@/hooks/usePatientSignOut";

import { LocalStorageKeys } from "@/common/constants";

import query from "@/Utils/request/query";
import { Organization } from "@/types/organization/organization";
import organizationApi from "@/types/organization/organizationApi";
import { TokenData } from "@/types/otp/otp";

const previewAreas = [
  {
    label: "Clinical command center",
    status: "Ready",
    detail: "OPD, IPD, emergency, rounds, orders and discharge workflows.",
  },
  {
    label: "Hospital operations",
    status: "Ready",
    detail: "Bed board, departments, services, worklists and daily patient flow.",
  },
  {
    label: "Revenue cycle",
    status: "Ready",
    detail: "Accounts, charge items, invoices, insurance policy and TPA desk.",
  },
  {
    label: "Management MIS",
    status: "Ready",
    detail: "Bed occupancy, facility services and operating review views.",
  },
] as const;

const previewModules = [
  {
    title: "OPD and doctor rounds",
    detail:
      "Registration, consultation, notes, orders, investigations and follow-up decisions.",
  },
  {
    title: "IPD and emergency",
    detail:
      "Admission, ward/bed assignment, emergency triage, observation and visit history.",
  },
  {
    title: "Billing + TPA",
    detail:
      "Patient account, charge items, draft invoice, policy and TPA workflow data.",
  },
  {
    title: "LIS + diagnostics",
    detail:
      "Lab order, sample accession, analyzer result import and diagnostic review foundation.",
  },
  {
    title: "OT + Blood bank",
    detail:
      "OT schedule, pre-op checklist, blood request, reservation and compatibility data.",
  },
  {
    title: "Pharmacy + inventory + MIS",
    detail:
      "Pharmacy/inventory service desk, bed occupancy and management operating views.",
  },
] as const;

const previewPatients = [
  {
    id: "OPD-001",
    name: "Sample OPD patient",
    age: "Adult",
    unit: "OPD room",
    issue: "Follow-up review and discharge planning",
    owner: "Attending doctor",
    status: "OPD consultation",
  },
  {
    id: "IPD-001",
    name: "Sample IPD patient",
    age: "Adult",
    unit: "Surgery ward",
    issue: "IPD pre-op and billing handoff",
    owner: "Attending doctor",
    status: "IPD case",
  },
  {
    id: "ER-001",
    name: "Sample emergency patient",
    age: "Child",
    unit: "Emergency bay",
    issue: "Emergency triage and observation case",
    owner: "Attending doctor",
    status: "Emergency case",
  },
] as const;

const { customLogo, stateLogo, mainLogo } = careConfig;

export function LandingPage() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOrganization, setSelectedOrganization] =
    useState<Organization | null>(null);
  const signOut = usePatientSignOut();
  const tokenData: TokenData = JSON.parse(
    localStorage.getItem(LocalStorageKeys.patientTokenKey) || "{}",
  );

  const isLoggedIn =
    tokenData.token &&
    Object.keys(tokenData).length > 0 &&
    dayjs(tokenData.createdAt).isAfter(dayjs().subtract(14, "minutes"));
  const { data: organizationsResponse } = useQuery({
    queryKey: ["organizations", "level", "1"],
    queryFn: query(organizationApi.getPublicOrganizations, {
      queryParams: { level_cache: 1 },
    }),
  });

  const organizations = organizationsResponse?.results || [];

  const filteredOrganizations = organizations.filter((organization) =>
    organization.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const orgType = organizations[0]?.metadata?.govt_org_type
    ? t(
        `SYSTEM__govt_org_type__${organizations[0]?.metadata?.govt_org_type}`,
      ).toLowerCase()
    : t("facility").toLowerCase();

  const inputRef = useRef<HTMLInputElement>(null);
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setSelectedOrganization(null);
    setIsOpen(true);
  };

  const handleInputClick = () => {
    setIsOpen(true);
    if (selectedOrganization) {
      setSearchQuery(selectedOrganization.name);
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const searchContainer = document.querySelector("[data-search-container]");

      if (!searchContainer?.contains(target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (selectedOrganization) {
      params.append("organization", selectedOrganization.id.toString());
    }
    navigate(`/facilities?${params.toString()}`);
  };

  const handleOrganizationSelect = (value: string) => {
    const organization = organizations.find(
      (o) => o.name.toLowerCase() === value.toLowerCase(),
    );
    if (organization) {
      setSelectedOrganization(organization);
      setSearchQuery("");
      setIsOpen(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 px-4 py-4 md:px-6 md:py-5">
      {/* Main Content  */}
      {isLoggedIn && (
        <header className="w-full">
          <div className="flex justify-end items-center gap-2">
            <Button
              variant="ghost"
              className="text-sm font-medium hover:bg-gray-100 px-6"
              onClick={() => navigate("/patient/home")}
            >
              {t("home")}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Avatar name={"User"} className="size-7 rounded-full" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem className="text-xs text-gray-500">
                  <span className="font-medium">{tokenData.phoneNumber}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-red-600 focus:text-red-600 cursor-pointer"
                  onClick={signOut}
                >
                  <CareIcon icon="l-signout" className="mr-2 size-4" />
                  {t("sign_out")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
      )}
      <main className="flex w-full max-w-3xl flex-1 flex-col items-center justify-center gap-4 py-3 md:gap-5 md:py-5 mx-auto">
        {/* Logo Section */}
        <div className="w-full flex flex-col items-center mt-2 md:mt-0">
          {stateLogo && stateLogo.dark && (
            <div className="mb-2">
              <img
                src={stateLogo.dark}
                alt="SEAES HMS"
                className="h-16 md:h-24 w-auto"
              />
            </div>
          )}

          {(customLogo || mainLogo) && (
            <div className="mb-2 md:mb-4">
              <img
                src={customLogo?.dark ?? mainLogo?.dark}
                alt="SEAES HMS"
                className="h-14 md:h-16 w-auto"
              />
            </div>
          )}
        </div>

        <div className="mb-5 max-w-2xl px-4 text-center">
          <p className="text-xs font-semibold uppercase text-primary-600">
            SEAES hospital management system
          </p>
          <h1 className="mt-2 text-3xl font-black text-gray-950 md:text-4xl">
            SEAES HMS
          </h1>
          <p className="mt-2 text-sm font-medium text-gray-600 md:text-base">
            Self-hosted hospital management for patient flow, clinical work,
            billing, diagnostics, inventory and management review.
          </p>
        </div>

        <section className="w-full rounded-lg border border-slate-200 bg-white px-4 py-5 shadow-sm sm:px-6 md:px-8">
          <div className="grid gap-5">
            <div>
              <div className="mb-3 inline-flex rounded-md bg-blue-50 px-3 py-1 text-xs font-semibold uppercase text-blue-700">
                Live HMS preview
              </div>
              <h1 className="text-3xl font-black text-slate-950 md:text-5xl">
                Run hospital operations from one owned system
              </h1>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600 md:text-base">
                SEAES HMS brings the hospital front desk, clinical team,
                diagnostics, OT, blood bank, billing desk and management view
                into a single self-hosted workflow. This preview is loaded with
                sample records so teams can inspect the system without
                touching real patient data.
              </p>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {previewAreas.map((item) => (
                  <div
                    key={item.label}
                    className="rounded-lg border border-slate-200 bg-slate-50 p-4"
                  >
                    <div className="text-xs font-semibold uppercase text-blue-700">
                      {item.status}
                    </div>
                    <div className="mt-1 text-sm font-semibold text-slate-800">
                      {item.label}
                    </div>
                    <div className="mt-1 text-xs leading-5 text-slate-500">
                      {item.detail}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
              <p className="text-xs font-semibold uppercase text-orange-700">
                What the preview includes
              </p>
              <h2 className="mt-2 text-xl font-bold text-slate-950">
                A working HMS workspace, not an empty shell
              </h2>
              <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-6 text-slate-700">
                <li>Sample OPD, IPD and emergency patient journeys</li>
                <li>Doctor workspace, notes, orders and visit actions</li>
                <li>Discharge, billing, invoice, insurance and TPA preview</li>
                <li>LIS, OT, blood bank, services, pharmacy/inventory and MIS data</li>
              </ul>
              <p className="mt-4 rounded-lg bg-white p-3 text-xs font-medium text-slate-600">
                Private working preview. Not production. Do not enter real
                patient data.
              </p>
            </div>
          </div>
        </section>

        <section className="grid w-full gap-4">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase text-blue-700">
              Clinical flow
            </p>
            <h2 className="mt-2 text-2xl font-black text-slate-950">
              Illustrative patient journey
            </h2>
            <div className="mt-4 space-y-3">
              {previewPatients.map((patient) => (
                <div
                  key={patient.id}
                  className="rounded-lg border border-slate-200 bg-slate-50 p-4"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="text-sm font-bold text-slate-950">
                        {patient.name} / {patient.age}
                      </div>
                      <div className="text-xs font-medium text-slate-500">
                        {patient.id} / {patient.unit}
                      </div>
                    </div>
                    <span className="rounded-md bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">
                      Sample case
                    </span>
                  </div>
                  <div className="mt-3 text-sm text-slate-700">
                    {patient.issue}
                  </div>
                  <div className="mt-2 text-xs text-slate-500">
                    {patient.owner} / {patient.status}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase text-blue-700">
              HMS module map
            </p>
            <h2 className="mt-2 text-2xl font-black text-slate-950">
              Current module coverage
            </h2>
            <div className="mt-4 grid gap-3">
              {previewModules.map((module) => (
                <div
                  key={module.title}
                  className="rounded-lg border border-slate-200 bg-slate-50 p-4"
                >
                  <div className="text-sm font-bold text-slate-950">
                    {module.title}
                  </div>
                  <div className="mt-2 text-xs leading-5 text-slate-600">
                    {module.detail}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Search Section */}
        <div className="w-full max-w-[680px] mx-auto rounded-lg border border-slate-200 bg-white/95 px-4 py-4 shadow-sm sm:px-6">
          <div className="text-center mb-4 space-x-1">
            <span className="text-sm md:text-base block sm:inline">
              <Trans
                i18nKey="search_facilities"
                components={{
                  strong: <strong />,
                }}
              />
            </span>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-2">
            <div className="relative w-full sm:w-9/12" data-search-container>
              <div className="rounded-lg border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="flex items-center px-2 bg-white rounded-lg">
                  <CareIcon icon="l-search" className="size-5 text-gray-400" />
                  <input
                    ref={inputRef}
                    type="text"
                    value={
                      selectedOrganization
                        ? selectedOrganization.name
                        : searchQuery
                    }
                    onChange={handleSearchChange}
                    onClick={handleInputClick}
                    placeholder={t(`landing_search_placeholder`, {
                      orgType,
                    })}
                    className="w-full border-0 bg-transparent px-3 py-2 text-sm outline-hidden placeholder:text-gray-500 cursor-pointer shadow-none ring-0"
                  />
                  {(searchQuery || selectedOrganization) && (
                    <Button
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSearchQuery("");
                        setSelectedOrganization(null);
                      }}
                      className="p-1 hover:bg-transparent"
                      aria-label="Clear facility search"
                    >
                      <CareIcon
                        icon="l-times"
                        className="size-4 text-gray-400"
                      />
                    </Button>
                  )}
                </div>
              </div>
              {isOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 rounded-md border border-gray-200 bg-white shadow-lg z-10">
                  <Command>
                    <CommandGroup className="overflow-y-auto max-h-60 md:max-h-80">
                      {filteredOrganizations.length === 0 ? (
                        <CommandEmpty>{t("search_no_results")}</CommandEmpty>
                      ) : (
                        filteredOrganizations.map((organization) => (
                          <CommandItem
                            key={organization.id}
                            value={organization.name.toLowerCase()}
                            onSelect={() => {
                              handleOrganizationSelect(organization.name);
                            }}
                            className="cursor-pointer"
                          >
                            {organization.name}
                          </CommandItem>
                        ))
                      )}
                    </CommandGroup>
                  </Command>
                </div>
              )}
            </div>
            {/* Search Button */}
            <Button
              variant="primary_gradient"
              className="w-full sm:w-3/12"
              onClick={handleSearch}
              disabled={!selectedOrganization}
            >
              <span className="bg-linear-to-b from-white/15 to-transparent"></span>
              {t("search_button")}
            </Button>
          </div>
        </div>

        {/* Centered Dots Image */}
        <div className="flex justify-center my-3 md:my-4">
          <img src="/images/dots.svg" alt="" />
        </div>

        {/* Login Section */}
        {!isLoggedIn && (
          <div className="w-full max-w-[680px] flex flex-col items-center justify-center rounded-lg border border-slate-200 bg-white/95 p-4 shadow-sm">
            <div className="text-sm font-medium mb-4 md:mb-6 text-center">
              {t("login_already_registered")}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 w-full max-w-full justify-center">
              <div className="flex flex-col items-center justify-center gap-3 p-3 rounded-lg shadow-sm bg-white hover:shadow-md transition-all bg-[url('/images/staff_background.png')] bg-auto bg-center bg-no-repeat">
                <div className="rounded-full bg-green-100 m-2 p-1 aspect-square flex justify-center items-center border-2 border-white shadow-sm">
                  <CareIcon
                    icon="d-health-worker"
                    className="size-8 text-green-700"
                  />
                </div>
                <div className="flex flex-col items-center">
                  <Button
                    variant="outline"
                    className="w-full text-xs md:text-sm border border-primary-600 text-primary-700 hover:text-primary-800 font-semibold"
                    onClick={() => navigate(`/login?mode=staff`)}
                  >
                    {t("staff_login")}
                  </Button>
                  <p className="text-xs mt-2 w-full text-center">
                    {t("staff_login_description")}
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-center justify-center gap-3 p-3 rounded-lg shadow-sm bg-white hover:shadow-md transition-all bg-[url('/images/patient_background.png')] bg-auto bg-center bg-no-repeat">
                <div className="rounded-full bg-indigo-100 m-2 p-1 aspect-square flex justify-center items-center border-2 border-white shadow-sm">
                  <CareIcon
                    icon="d-patient"
                    className="size-8 text-indigo-700"
                  />
                </div>
                <div className="flex flex-col items-center">
                  <Button
                    variant="outline"
                    className="w-full text-xs md:text-sm border border-primary-600 text-primary-700 hover:text-primary-800 font-semibold"
                    onClick={() => navigate(`/login?mode=patient`)}
                  >
                    {t("patient_login")}
                  </Button>
                  <p className="text-xs mt-2 w-full text-center">
                    {t("patient_login_description")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
